import { Box } from '@mui/material';
import { DataGridPro, DataGridProProps, GridColDef } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import Link from 'next/link';
import { FeeTypeEnum } from 'parkchirp-shared/enums';
import React, { useEffect, useState } from 'react';

import { datagridFilterPanelAttributes } from '../../../utils';
import { InvoiceLineItemDetail } from './invoiceLineItemDetail';

const getInvoiceLineItemColumns = (createdByDailyReport): GridColDef[] => {
	const defaultColumns: GridColDef[] = [
		{
			field: 'feeName',
			headerName: 'Fee',
			width: 250,
			editable: false,
		},
		{
			field: 'parkerName',
			headerName: 'Parker',
			width: 150,
			editable: false,
		},
		{
			field: 'sublotName',
			headerName: 'Sublot',
			width: 150,
			editable: false,
		},
		{
			field: 'rateName',
			headerName: 'Rate',
			width: 200,
			editable: false,
		},
		{
			field: 'selectedDates',
			headerName: 'Selected Dates',
			width: 200,
			editable: false,
		},
		{
			field: 'amount',
			headerName: 'Amount',
			width: 200,
			editable: false,
		},
		{
			field: 'balance',
			headerName: 'Balance',
			width: 200,
			editable: false,
		},
	];

	if (createdByDailyReport) {
		defaultColumns.push({
			field: 'dailyReportId',
			headerName: 'Daily Report',
			width: 200,
			editable: false,
			valueGetter: ({ row }) => row.dailyReport?.readableId,
			renderCell: (params) => {
				if (params.row.dailyReport?.readableId) {
					const { id, readableId } = params.row.dailyReport;
					return (
						<Link
							key={`daily-report-${id}`}
							href={`daily-reports/create-daily-report?id=${id}`}
							target='_blank'
							style={{ color: 'blue' }}
						>
							{readableId}
						</Link>
					);
				}

				return '';
			},
		});
	}

	return defaultColumns;
};

export const InvoiceLineItemsTable = ({
	lineItemsData,
	timezone,
	createdByDailyReport,
	lineItemsLoading,
	setShowSnackbar,
}) => {
	const [rows, setRows] = useState([]);

	useEffect(() => {
		let rowsToUse = [];
		let totalAmount = 0;
		let totalBalance = 0;

		if (lineItemsData?.getInvoiceLineItems?.length) {
			rowsToUse = lineItemsData.getInvoiceLineItems.map((data, index) => {
				let maybeStartDate = null;
				let maybeEndDate = null;
				// took out timezone because serviceDateStart and end don't have times
				if (data.serviceDateStart) {
					maybeStartDate = dayjs(data.serviceDateStart.slice(0, 10)).format('M/D/YYYY');
					maybeEndDate = dayjs(data.serviceDateEnd.slice(0, 10)).format('M/D/YYYY');
					// leaving this to retrofit previous data
				} else if (data.parker) {
					maybeStartDate = dayjs(Number(data.parker.selected_start_date)).utc().format('M/D/YYYY');
					maybeEndDate = dayjs(Number(data.parker.selected_end_date)).utc().format('M/D/YYYY');
				}
				const isVehicleSurcharge = data.fee.type === 'VEHICLE_SURCHARGE';
				let selectedDates = '-';

				if (!isVehicleSurcharge && maybeStartDate) {
					selectedDates = `${maybeStartDate} - ${maybeEndDate}`;
				}
				totalAmount += data.amount;
				totalBalance += data.balance;

				return {
					id: index,
					lineItemId: data.id,
					...(data.fee && { feeName: data.fee.name || `${FeeTypeEnum[data.fee.type]} Fee` }),
					...(data.parker && {
						parkerName: `${data.parker.first_name} ${data.parker.last_name}`,
						// selectedDates: `${maybeStartDate} - ${maybeEndDate}`,
					}),
					...(data.sublot && {
						sublotName: data.sublot.name,
					}),
					selectedDates,
					...(data.rate && { rateName: data.rate.name }),
					amount: (data.amount / 100).toLocaleString('en-US', {
						style: 'currency',
						currency: 'USD',
					}),
					balance: (data.balance / 100).toLocaleString('en-US', {
						style: 'currency',
						currency: 'USD',
					}),
					dailyReport: data.dailyReport,
				};
			});
		}
		setRows([
			...rowsToUse,
			{
				id: rowsToUse.length + 1,
				selectedDates: 'Total',
				amount: (totalAmount / 100).toLocaleString('en-US', {
					style: 'currency',
					currency: 'USD',
				}),
				balance: (totalBalance / 100).toLocaleString('en-US', {
					style: 'currency',
					currency: 'USD',
				}),
			},
		]);
	}, [lineItemsData, timezone]);

	const getDetailPanelContent = React.useCallback<NonNullable<DataGridProProps['getDetailPanelContent']>>(
		({ row }) => {
			// any normal row will have the below row present, the total
			// row will not have this field though. we have this if statement
			// to prevent the total row from having an expandable section
			if (row.lineItemId) {
				return (
					<InvoiceLineItemDetail
						{...{
							row,
							timezone,
							setShowSnackbar,
						}}
					/>
				);
			}
			return null;
		},
		[timezone],
	);

	const columns = getInvoiceLineItemColumns(createdByDailyReport);

	return (
		<Box
			sx={{
				height: '30%',
				width: '100%',
				paddingBottom: '40px',
			}}
			data-cy='invoice-line-items-table-container'
		>
			{createdByDailyReport && (
				<div style={{ marginLeft: '5px', marginBottom: '5px', fontSize: '14px' }}>
					NOTE: This invoice was automatically generated from a Daily Report
				</div>
			)}
			<DataGridPro
				className='invoiceLineItemsTable'
				rows={rows}
				columns={columns}
				loading={lineItemsLoading}
				initialState={{
					pagination: {
						paginationModel: {
							pageSize: 5,
						},
					},
				}}
				slotProps={{ filterPanel: { ...datagridFilterPanelAttributes } }}
				pageSizeOptions={[5]}
				sx={{ height: '50vh' }}
				// the total row will not have the lineItemId field,
				// so we give it a custom class
				getRowClassName={(params) => (params.row.lineItemId ? '' : 'totalRow')}
				getDetailPanelContent={getDetailPanelContent}
				getDetailPanelHeight={() => 'auto'}
				disableRowSelectionOnClick
			/>
		</Box>
	);
};
